import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import useInView from '../hooks/useInView';
import { useTranslation } from 'react-i18next';

const LatestBlog = () => {
  const { t, i18n } = useTranslation('common');
  const monthEn = { JAN: 'Jan', FEB: 'Feb', MAR: 'Mar', APR: 'Apr', MAY: 'May', JUN: 'Jun', JUL: 'Jul', AUG: 'Aug', SEP: 'Sep', OCT: 'Oct', NOV: 'Nov', DEC: 'Dec' };
  const monthIndex = { JAN: 0, FEB: 1, MAR: 2, APR: 3, MAY: 4, JUN: 5, JUL: 6, AUG: 7, SEP: 8, OCT: 9, NOV: 10, DEC: 11 };
  const lng = (i18n.language || 'en').split('-')[0];
  const fmtDate = new Intl.DateTimeFormat(lng, { year: 'numeric', month: 'short', day: 'numeric' });
  const fmtNum = new Intl.NumberFormat(lng);
  const titleBaseMap = {
    ar: 'عنوان المدونة',
    ur: 'بلاگ عنوان',
    en: 'BlogTitle',
    fr: 'Titre du blog',
    de: 'Blogtitel',
    es: 'Título del blog',
    pt: 'Título do blog',
    ja: 'ブログタイトル',
    hi: 'ब्लॉग शीर्षक',
    it: 'Titolo del blog',
    ru: 'Заголовок блога',
    zh: '博客标题'
  };
  const dateData = [
    { mon: 'MAR', day: 15, year: 2024 },
    { mon: 'MAR', day: 10, year: 2024 },
    { mon: 'MAR', day: 5, year: 2024 },
    { mon: 'MAR', day: 1, year: 2024 },
    { mon: 'FEB', day: 26, year: 2024 },
    { mon: 'FEB', day: 20, year: 2024 },
    { mon: 'FEB', day: 15, year: 2024 },
    { mon: 'FEB', day: 10, year: 2024 },
    { mon: 'FEB', day: 5, year: 2024 }
  ];
  const images = [
    "/assets/imgs/Home/Latest Blog/1.png",
    "/assets/imgs/Home/Latest Blog/2.png",
    "/assets/imgs/Home/Latest Blog/3.png"
  ];
  const blogs = dateData.map((d, i) => {
    const dateObj = new Date(d.year, monthIndex[d.mon], d.day);
    const baseFromResource = i18n.getResource(lng, 'common', 'home.latestBlog.cardTitleBase');
    const titleBase = (typeof baseFromResource === 'string' && baseFromResource) || titleBaseMap[lng] || 'BlogTitle';
    return {
      id: i + 1,
      title: `${titleBase} ${String(i + 1).padStart(2, '0')}`,
      date: fmtDate.format(dateObj) || `${t(`months.${d.mon}`, { defaultValue: monthEn[d.mon] })} ${d.day}, ${d.year}`,
      image: images[i % images.length]
    };
  });
  
  const trackRef = useRef(null);
  const [visible, setVisible] = useState([]);
  const [sectionRef, inView] = useInView({ threshold: 0.15 });
  const intervalRef = useRef(null);

  useEffect(() => {
    if (!inView) return;
    const timers = blogs.map((_, i) =>
      setTimeout(() => {
        setVisible((v) => {
          const nv = [...v];
          nv[i] = true;
          return nv;
        });
      }, i * 120)
    );
    return () => timers.forEach(clearTimeout);
  }, [inView]);

  const getCardStep = () => {
    const el = trackRef.current;
    if (!el) return 360;
    const firstCard = el.querySelector('[data-card]');
    const gap = 24; // px (gap-6)
    return firstCard ? firstCard.offsetWidth + gap : 360;
  };

  // Auto slide by 3 cards RTL
  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;

    const advance = () => {
      const step = getCardStep() * 3;
      const maxScroll = el.scrollWidth - el.clientWidth;
      const nextLeft = Math.min(el.scrollLeft + step, maxScroll);
      el.scrollTo({ left: nextLeft, behavior: 'smooth' });

      // Loop back to start when reaching end
      if (nextLeft >= maxScroll - 2) {
        // allow smooth scroll to finish before jump
        setTimeout(() => el.scrollTo({ left: 0, behavior: 'auto' }), 600);
      }
    };

    intervalRef.current = setInterval(advance, 3500);

    // Pause on hover
    const pause = () => intervalRef.current && clearInterval(intervalRef.current);
    const resume = () => {
      pause();
      intervalRef.current = setInterval(advance, 3500);
    };
    el.addEventListener('mouseenter', pause);
    el.addEventListener('mouseleave', resume);

    return () => {
      pause();
      el.removeEventListener('mouseenter', pause);
      el.removeEventListener('mouseleave', resume);
    };
  }, []);

  return (
    <section ref={sectionRef} className="bg-cream py-16 lg:py-24" id="blog">
      <div className="container mx-auto px-8 lg:px-16">
        {/* Section Title */}
        <div className="text-center mb-12">
          <h2 className="text-4xl lg:text-5xl font-bold text-text-dark mb-0">
            {t('home.latestBlog.title')}
          </h2>
        </div>

        {/* Slider */}
        <div className="relative">
          <div
            ref={trackRef}
            className="flex gap-6 overflow-x-auto snap-x snap-mandatory scroll-smooth no-scrollbar pr-2"
          >
          {blogs.map((blog, i) => (
            <div key={blog.id} className="group cursor-pointer snap-start" data-card>
              <div className={`relative overflow-hidden rounded-lg shadow-lg transition-all duration-700 ease-out group-hover:-translate-y-2 group-hover:shadow-2xl min-w-[280px] md:min-w-[360px] lg:min-w-[420px] ${visible[i] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`} style={{ transitionDelay: `${100 + i * 120}ms` }}>
                {/* Blog Image with Overlay */}
                <div className="relative h-80 overflow-hidden">
                  <img 
                    src={blog.image} 
                    alt={blog.title}
                    className={`w-full h-full object-cover transition-transform duration-[4000ms] ${visible[i] ? 'scale-110' : 'scale-100'} group-hover:scale-115`}
                  />
                  {/* Dark Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
                  
                  {/* Content Overlay (centered) */}
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <div className="w-full flex flex-col items-center text-center">
                      {/* Blog Title */}
                      <h3 className="font-poppins font-medium text-2xl text-white leading-tight mb-3">
                        {blog.title}
                      </h3>

                      {/* Meta Row: date + icons (inline and centered) */}
                      <div className="flex items-center justify-center gap-5">
                        {/* Date */}
                        <span className="font-poppins text-sm text-white/90">
                          {blog.date}
                        </span>
                        
                        {/* Like (thumbs up) */}
                        <div className="flex items-center text-white">
                          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="opacity-90 block">
                            <path d="M14 9V5a3 3 0 0 0-3-3l-1 5-4 4v10h9.28a2 2 0 0 0 2-1.72l1.11-7.77A2 2 0 0 0 16.42 9H14z"/>
                            <path d="M7 22V11"/>
                          </svg>
                          <span className="text-sm ml-1 leading-none">{fmtNum.format(0)}</span>
                        </div>

                        {/* Share (paper plane) */}
                        <div className="flex items-center text-white">
                          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="opacity-90 block">
                            <path d="M22 2L11 13"/>
                            <path d="M22 2l-7 20-4-9-9-4 20-7z"/>
                          </svg>
                          <span className="text-sm ml-1 leading-none">{fmtNum.format(0)}</span>
                        </div>

                        {/* Comment (chat bubble with lines) */}
                        <div className="flex items-center text-white">
                          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="opacity-90 block">
                            <path d="M21 15a4 4 0 0 1-4 4H7l-4 4V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z"/>
                            <path d="M8 9h8"/>
                            <path d="M8 13h6"/>
                          </svg>
                          <span className="text-sm ml-1 leading-none">{fmtNum.format(0)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
          </div>
        </div>

        {/* View More Button */}
        <div className="text-center mt-10">
          <Link 
            to="../blog" 
            className="inline-block bg-brown hover:bg-brown-dark text-cream px-10 py-3 rounded-md text-sm font-normal uppercase tracking-wide transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
          >
            {t('home.latestBlog.viewMore')}
          </Link>
        </div>
      </div>
    </section>
  );
};

export default LatestBlog;
